import { get, isEmpty, has } from 'lodash';
import cns from 'classnames';
import { calculatePercentage } from './NumberUtils';
import { LEARNING_ASSET_DURATIONS } from '../Constants';
import { overlayImageIds } from './ImageUtils';

const createUserLearningAssetObjects = (userLearningAssets = []) => {
    const resultItems = [];

    userLearningAssets.forEach((userLearningAsset) => {
        resultItems.push({
            description: get(userLearningAsset, 'track.subtitle', ''),
            name: get(userLearningAsset, 'track.name', ''),
            assetId: userLearningAsset._id,
            updatedOn: userLearningAsset.updatedOn,
            image: get(userLearningAsset, 'track.thumbnailImageUrl', ''),
            url: `/workspace/${userLearningAsset._id}`,
            duration: get(userLearningAsset, 'track.duration', ''),
            isSolo: (get(userLearningAsset, 'track.participants', []) || []).length <= 1,
            completedSectionItems: get(userLearningAsset, 'completedSectionItems', {}),
            type: get(userLearningAsset, 'track.type', '')
        });
    });
    return resultItems;
};
export const isSoloLearningAsset = (learningAssetType) => {
    if (
        learningAssetType === 'TRACK' ||
        learningAssetType === 'IMPACT_PROJECT' ||
        learningAssetType === 'FEEDBACK'
    ) {
        return false;
    }
    return true;
};
const canCalculateCompleteness = (userLearningAsset) => has(userLearningAsset, 'completedSectionItems');

const computeUserLearningAssetCompleteness = (userLearningAsset) => {
    const userLearningAssetTotalSectionItems = get(userLearningAsset, 'track.totalSectionItemCount', 0);
    if (userLearningAssetTotalSectionItems <= 0) {
        return 0;
    }

    if (userLearningAsset.isCompleted) {
        return 100;
    }

    const completedSectionItems = (userLearningAsset && userLearningAsset.completedSectionItems) || {};

    if (Object.keys(completedSectionItems).length > 0) {
        return 1;
    }

    return 0;
};

const makeTimeDisplayForUserCollection = (minutes) => {
    const timeObject = {
        value: '',
        units: ''
    };

    if (Number.isNaN(Number(minutes)) || minutes === undefined || minutes === null || minutes === '') {
        return timeObject;
    }

    if (minutes < 120) {
        timeObject.value = minutes;
        timeObject.units = 'MINUTES';
    } else if (minutes <= 600) {
        timeObject.value = Math.round(minutes / 60);
        timeObject.units = 'HOURS';
    } else {
        timeObject.value = '10+';
        timeObject.units = 'HOURS';
    }

    return timeObject;
};

const getCompletionStatusForUserCollection = (userCollectionContent) => {
    const defaultObject = {
        percentComplete: 0,
        completedLearningAssets: 0,
        timeSpentOnCollection: 0,
        totalTimeForCollection: 0,
        totalLearningAssets: 0,
        assetToContinue: '',
        lastUpdated: '',
        hasCompletedData: false
    };

    if (isEmpty(userCollectionContent)) {
        return defaultObject;
    }

    let assetDurationId;
    let assetDurationInMinutes;
    let lastAssetUpdateTime;
    let completedSectionItems;

    const result = userCollectionContent.reduce((acc, asset) => {
        assetDurationId = get(asset, 'track.durationId', '');
        assetDurationInMinutes = get(LEARNING_ASSET_DURATIONS(), `${assetDurationId}.inMinutes`, 0);
        acc.totalTimeForCollection += assetDurationInMinutes;
        acc.totalLearningAssets += 1;

        if (asset.isCompleted) {
            acc.completedLearningAssets += 1;
            acc.timeSpentOnCollection += assetDurationInMinutes;
        } else if (!acc.assetToContinue) {
            acc.assetToContinue = asset._id;
        }

        completedSectionItems = get(asset, 'completedSectionItems', {});
        if (!acc.hasCompletedData && (!isEmpty(completedSectionItems) || asset.isCompleted)) {
            acc.hasCompletedData = true;
        }

        lastAssetUpdateTime = new Date(asset.updatedOn);

        if (!acc.lastUpdated || lastAssetUpdateTime > acc.lastUpdated) {
            acc.lastUpdated = lastAssetUpdateTime;
        }

        return acc;
    }, defaultObject);
    result.percentComplete = calculatePercentage(result.completedLearningAssets, result.totalLearningAssets);
    return result;
};

const attachLockedAndDisabledPropsToCard = (props) => ({
    ...props,
    disableBookmark: true,
    disableClick: true,
    isReadOnly: true,
    className: cns(props.className, 'disabled'),
    overlayProps: {
        overlayId: overlayImageIds.locked,
        dimension: {
            h: 250,
            w: 250
        }
    }
});

const unlockAndCompleteContent = (completedULAId, userCollections) => {
    if (!completedULAId || !userCollections || !userCollections.length) {
        return { userCollections: [], completedCollectionIds: [] };
    }
    const newUserCollections = [...userCollections];
    const completedCollectionIds = [];
    const result = newUserCollections.reduce((acc, userCollection) => {
        let completionFlag = 1;
        let isRelevantCollection = false;
        userCollection.content = userCollection.content.map((content) => {
            if (content.userLearningAssetId === completedULAId) {
                content.isCompleted = true;
                content.isLocked = false;
                content.completedDateTime = new Date();
                isRelevantCollection = true;
            }
            if (!content.isCompleted && completionFlag) {
                content.isLocked = false;
                completionFlag = 0;
            }

            return content;
        });
        if (completionFlag && isRelevantCollection) {
            completedCollectionIds.push(userCollection._id);
            userCollection.status = 'COMPLETED';
        }
        if (isRelevantCollection) {
            acc.push(userCollection);
        }
        return acc;
    }, []);
    return { userCollections: result, completedCollectionIds };
};

const completePathwayContent = (userCollectionIds = [], userPathways = []) => {
    if (!userCollectionIds.length || !userPathways.length) {
        return { completedPathways: [] };
    }
    const newUserPathways = [...userPathways];
    const userCollectionDict = userCollectionIds.reduce((acc, id) => {
        acc[id] = true;
        return acc;
    }, {});
    let currentPathwayCompletedNow = {};
    const completedUserPathways = newUserPathways.reduce((acc, userPathway) => {
        let completionFlag = 1;
        let isRelevant = 0;
        userPathway.content = userPathway.content.map((content) => {
            if (userCollectionDict[content.userSubscriptionId]) {
                content.isCompleted = true;
                content.status = 'COMPLETED';
                isRelevant = 1;
            }
            if (!content.isCompleted && completionFlag) {
                completionFlag = 0;
            }
            return content;
        });
        if (completionFlag && isRelevant) {
            // if current pathway and is completed at this time, set pathway as currentPathwayCompletedNows
            if (!userPathway.isCompleted) {
                if (
                    isEmpty(currentPathwayCompletedNow) ||
                    new Date(userPathway.createdOn) < new Date(currentPathwayCompletedNow.createdOn)
                ) {
                    currentPathwayCompletedNow = { ...userPathway, state: 'COMPLETED', isCompleted: true };
                }
            }
            userPathway.status = 'COMPLETED';
            userPathway.isCompleted = true;
            acc.push(userPathway);
        }
        return acc;
    }, []);
    return {
        completedPathways: completedUserPathways,
        currentPathwayCompletedNow
    };
};

export {
    createUserLearningAssetObjects,
    canCalculateCompleteness,
    computeUserLearningAssetCompleteness,
    getCompletionStatusForUserCollection,
    makeTimeDisplayForUserCollection,
    attachLockedAndDisabledPropsToCard,
    unlockAndCompleteContent,
    completePathwayContent
};
